"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Download, Facebook, AlertCircle, Video, Music } from "lucide-react"

const FacebookDownloader = () => {
  const [url, setUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [videoInfo, setVideoInfo] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setVideoInfo(null)

    const myHeaders = new Headers()
    myHeaders.append("x-apihub-key", "dC-X6X1n75W9ul7oEz2tOmvuaww1WLB3cGiKWsfsq057qO4-Ay")
    myHeaders.append("x-apihub-host", "Video-Downloader.allthingsdev.co")
    myHeaders.append("x-apihub-endpoint", "7e8a1676-836a-440b-a702-7758d89c110d")

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    }

    try {
      const response = await fetch(
        `https://Video-Downloader.proxy-production.allthingsdev.co/facebook-download?url=${encodeURIComponent(url)}`,
        requestOptions,
      )
      const result = await response.json()

      if (response.ok) {
        setVideoInfo(result)
      } else {
        throw new Error(result.message || "Failed to fetch video information")
      }
    } catch (error) {
      console.error("Error:", error)
      setError(error.message || "An error occurred while processing your request")
    } finally {
      setLoading(false)
    }
  }

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  return (
    <div className="max-w-4xl mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <Facebook className="inline-block text-blue-600 mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Facebook Video Downloader</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Download your favorite Facebook videos and reels easily!
        </p>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="url"
            placeholder="Paste Facebook video URL here"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            className="flex-grow px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-300 flex items-center justify-center"
          >
            {loading ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              <>
                <Download className="mr-2" size={18} />
                Download
              </>
            )}
          </button>
        </div>
      </motion.form>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-8"
          role="alert"
        >
          <div className="flex items-center">
            <AlertCircle className="mr-2" size={18} />
            <p>{error}</p>
          </div>
        </motion.div>
      )}

      {videoInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4 dark:text-white">{videoInfo.title}</h2>
            <div className="flex items-center mb-4">
              <Video className="mr-2 text-blue-600" size={18} />
              <span className="text-gray-600 dark:text-gray-300">Duration: {formatDuration(videoInfo.duration)}</span>
            </div>
            <img
              src={videoInfo.thumbnailUrl || "/placeholder.svg"}
              alt="Video Thumbnail"
              className="w-full rounded-lg mb-4"
            />
            <div className="grid gap-4">
              {videoInfo.videoFormats.map((format) => (
                <a
                  key={format.formatId}
                  href={format.url}
                  download
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center"
                >
                  <Download className="mr-2" size={18} />
                  Download {format.label} ({format.quality})
                </a>
              ))}
              {videoInfo.audioFormats.map((format) => (
                <a
                  key={format.formatId}
                  href={format.url}
                  download
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors duration-300 flex items-center justify-center"
                >
                  <Music className="mr-2" size={18} />
                  Download {format.label}
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default FacebookDownloader

